import { Box, TableCell, TableRow } from "@mui/material";
import {
    DotLegalButton,
    DotLegalDeleteDialog,
    DotLegalEmptyState,
    DotLegalInformationTooltip,
    DotLegalOverflowMenu,
    DotLegalPaper,
    DotLegalTableWithControls,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { FrameworkTemplateControlTableRowModel, FrameworkTemplateControlViewModel } from "../FrameworkTemplateOverview.types.ts";
import React from "react";
import NoSharings from "../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { useFrameworkTemplateControl } from "./FrameworkTemplateControl.hooks.ts";
import FrameworkTemplateControlDialog from "./frameworkTemplateControlDialog/FrameworkTemplateControlDialog.tsx";
import { FrameworkTemplateControlSaveModel } from "./frameworkTemplateControlDialog/FrameworkTemplateControlDialog.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { sortIds } from "../../../common/utilities.ts";

export interface FrameworkTemplateControlProps {
    frameworkTemplateId: string;
    isLoading?: boolean;
    controls: Array<FrameworkTemplateControlViewModel> | undefined;
    refetch: () => void;
}

function FrameworkTemplateControl(props: FrameworkTemplateControlProps) {
    let userContext = useUserContext();
    const { showAddEditDialog, setShowAddEditDialog, selectedControl, setSelectedControl, controlToDelete, setControlToDelete, onDeleteControl } =
        useFrameworkTemplateControl(props);

    function getHeaders() {
        let headers = Array<ITableHeader<FrameworkTemplateControlTableRowModel>>();
        headers.push({
            property: "customId",
            text: "ID",
            align: "left",
            showOnMobile: true,
            sortOverride: (a, b, order) => sortIds(a.customId, b.customId, order === "desc"),
        });
        headers.push({ property: "name", text: "Control", align: "left", showOnMobile: true });
        headers.push({ property: "category", text: "Category", align: "left", showOnMobile: true });
        headers.push({ property: "implementationGroup", text: "Implementation group", align: "left", showOnMobile: true });
        headers.push({ property: "noOfActivities", text: "Annual Wheel Activities", align: "left", showOnMobile: true });

        return headers;
    }

    const onEditClick = (row: FrameworkTemplateControlTableRowModel) => {
        setShowAddEditDialog(true);
        setSelectedControl(row);
    };

    return (
        <>
            <DotLegalPaper>
                {/* This box is needed so the btn will be wrapped inside the paper */}
                <Box>
                    {props.isLoading || (props.controls && props.controls.length > 0) ? (
                        <DotLegalTableWithControls
                            getUserSpecificPageLength={() => 1000}
                            labelRowsPerPage={""}
                            hideRowsPerPage
                            hasOverflowMenu
                            labelSearch={"Search"}
                            noOptionsLabel={"No options"}
                            headers={getHeaders()}
                            defaultOrderBy={"customId"}
                            paginationLabelOf={"of"}
                            emptyText={"No controls found"}
                            isLoading={props.isLoading}
                            data={convertToTableData(props.controls ?? [])}
                            renderRow={(row, index) => {
                                return (
                                    <TableRow key={row.id + index} onClick={() => onEditClick(row)}>
                                        <TableCell>{row.customId}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                {row.name} <DotLegalInformationTooltip text={row.description} />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {row.categoryCustomId && row.categoryCustomId + " "}
                                            {row.category.name}
                                        </TableCell>
                                        <TableCell>{row.implementationGroup}</TableCell>
                                        <TableCell>{row.noOfActivities}</TableCell>
                                        <TableCell align={"right"}>
                                            <DotLegalOverflowMenu
                                                items={[
                                                    { menuItem: "Edit", onClick: () => onEditClick(row) },
                                                    { menuItem: "Delete", onClick: () => setControlToDelete(row) },
                                                ]}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }}
                        />
                    ) : (
                        <DotLegalEmptyState icon={<NoSharings />} text={"No controls found"} />
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <DotLegalButton buttonType={"primary"} onClick={() => setShowAddEditDialog(true)}>
                            Add control
                        </DotLegalButton>
                    </Box>
                </Box>
            </DotLegalPaper>

            {showAddEditDialog && (
                <FrameworkTemplateControlDialog
                    onCloseDialog={() => {
                        setShowAddEditDialog(false);
                        setSelectedControl(undefined);
                    }}
                    onControlSaved={() => {
                        setShowAddEditDialog(false);
                        setSelectedControl(undefined);
                        props.refetch();
                    }}
                    frameworkTemplateId={props.frameworkTemplateId}
                    controlId={selectedControl?.id}
                    control={
                        selectedControl
                            ? new FrameworkTemplateControlSaveModel(
                                  selectedControl.customId,
                                  selectedControl.category.id,
                                  selectedControl.annualWheelActivities,
                                  selectedControl.languages,
                                  selectedControl.implementationGroup
                              )
                            : undefined
                    }
                />
            )}

            {controlToDelete && (
                <DotLegalDeleteDialog
                    open
                    headerText={"Delete control"}
                    alertBodyText={"Are you sure you want to delete this control? This action cannot be undone."}
                    warningTranslation={""}
                    deleteBtnText={"Delete"}
                    cancelBtnText={"Cancel"}
                    onDialogClose={() => setControlToDelete(undefined)}
                    onOkClick={onDeleteControl}
                />
            )}
        </>
    );

    function convertToTableData(controls: Array<FrameworkTemplateControlViewModel>): Array<FrameworkTemplateControlTableRowModel> {
        return controls.map((x) => {
            let lang = x.languages.find((l) => l.twoLetterIsoLanguage == userContext.language)!;
            return {
                ...x,
                categoryName: x.category.name,
                name: lang.name,
                description: lang.description ?? "",
                noOfActivities: x.annualWheelActivities.length,
            };
        });
    }
}

export default FrameworkTemplateControl;
